import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Inbox, ChevronDown, Mail, Phone, Clock, FileText, MessageCircle, Home as HomeIcon, Users, Archive, ArchiveRestore } from "lucide-react";

const FORM_TYPES = [
  {
    key: "valuation",
    label: "Valuation Request",
    icon: HomeIcon,
    fields: [
      { label: "Full name", key: "fullName" },
      { label: "Email", key: "email" },
      { label: "Phone", key: "phone" },
      { label: "Postcode", key: "postcode" },
      { label: "Bedrooms", key: "bedrooms" },
      { label: "Property type", key: "propertyType" },
      { label: "Full address", key: "address" },
      { label: "Condition", key: "condition" },
      { label: "Parking", key: "parking" },
      { label: "Other properties", key: "hasOtherProperties" },
      { label: "Listing link", key: "listingLink" },
      { label: "Referral consent", key: "referralConsent" },
      { label: "Mode", key: "mode" },
    ],
  },
  {
    key: "interiors",
    label: "Interiors Quote",
    icon: FileText,
    fields: [
      { label: "Full name", key: "fullName" },
      { label: "Email", key: "email" },
      { label: "Phone", key: "phone" },
      { label: "Property address", key: "address" },
      { label: "Start date", key: "startDate" },
      { label: "Comments", key: "comments" },
    ],
  },
  {
    key: "referral",
    label: "Referral",
    icon: Users,
    fields: [
      { label: "Name", key: "name" },
      { label: "Email", key: "email" },
      { label: "Phone", key: "phone" },
      { label: "Postcode / area", key: "postcode" },
      { label: "Property type", key: "propertyType" },
      { label: "Bedrooms", key: "bedrooms" },
      { label: "Reward choice", key: "reward" },
    ],
  },
  {
    key: "support",
    label: "Support Enquiry",
    icon: MessageCircle,
    fields: [
      { label: "Name", key: "fullName" },
      { label: "Email", key: "email" },
      { label: "Subject", key: "subject" },
      { label: "Message", key: "message" },
    ],
  },
];

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function renderFieldValue(value) {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "True" : "False";
  return String(value);
}

// Partial submissions from the enrichment modal — mode "enrichment".
function isPartial(lead) {
  try { return JSON.parse(lead.form_data || "{}").mode === "enrichment"; } catch { return false; }
}

export default function FormSubmissions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [activeType, setActiveType] = useState("valuation");
  const [expandedId, setExpandedId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [hidePartial, setHidePartial] = useState(false);

  useEffect(() => {
    base44.auth.me().then(user => {
      if (user?.role !== "admin") { navigate("/"); return null; }
      return base44.entities.Lead.list("-created_date", 500);
    }).then(res => {
      if (res) setLeads(res);
      setLoading(false);
    }).catch(() => { navigate("/"); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
    </div>
  );

  const activeFormType = FORM_TYPES.find(ft => ft.key === activeType);
  const typeLeads = leads.filter(l => l.form_type === activeType && (showArchived ? !!l.archived : !l.archived) && !(hidePartial && isPartial(l)));

  const parseFormData = (lead) => {
    try { return JSON.parse(lead.form_data || "{}"); } catch { return {}; }
  };

  const setArchived = async (lead, archived) => {
    try {
      await base44.entities.Lead.update(lead.id, { archived });
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, archived } : l));
    } catch (e) { console.error("Archive failed", e); }
  };

  const ActiveIcon = activeFormType.icon;

  return (
    <div style={{ background: "#F7F7F7", minHeight: "100vh", paddingTop: 68 }}>
      {/* Page header */}
      <div style={{ background: "#fff", padding: "40px clamp(20px,5vw,80px)", borderBottom: "1px solid #E8E8E8" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#888", marginBottom: 8, marginTop: 0 }}>Admin Tool</p>
          <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: "clamp(22px,3vw,34px)", color: "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>Form Submissions</h1>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px clamp(20px,5vw,80px) 80px" }}>
        {/* Form type tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" }}>
          {FORM_TYPES.map(ft => {
            const count = leads.filter(l => l.form_type === ft.key && !l.archived && !(hidePartial && isPartial(l))).length;
            const active = activeType === ft.key;
            const Icon = ft.icon;
            return (
              <button
                key={ft.key}
                onClick={() => { setActiveType(ft.key); setExpandedId(null); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 18px", borderRadius: 10, cursor: "pointer",
                  fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600,
                  border: active ? "1.5px solid #1A1A1A" : "1.5px solid #E5E5E5",
                  background: active ? "#1A1A1A" : "#fff",
                  color: active ? "#fff" : "#1A1A1A",
                  transition: "all 0.2s",
                }}
              >
                <Icon size={15} />
                {ft.label}
                <span style={{
                  background: active ? "rgba(255,255,255,0.2)" : "#F0F0F0",
                  color: active ? "#fff" : "#888",
                  borderRadius: 100, padding: "2px 8px", fontSize: 12, fontWeight: 700,
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "Inter, sans-serif", fontSize: 13, color: "#555", cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={hidePartial} onChange={e => { setHidePartial(e.target.checked); setExpandedId(null); }} style={{ width: 16, height: 16, cursor: "pointer" }} />
            Hide partial submissions
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "Inter, sans-serif", fontSize: 13, color: "#555", cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={showArchived} onChange={e => { setShowArchived(e.target.checked); setExpandedId(null); }} style={{ width: 16, height: 16, cursor: "pointer" }} />
            Show archived
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 24, alignItems: "start" }} className="fs-layout">
          {/* What's included panel */}
          <div style={{ background: "#fff", borderRadius: 14, padding: "24px", border: "1px solid #E8E8E8", position: "sticky", top: 90 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#F7F7F7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ActiveIcon size={16} color="#1A1A1A" />
              </div>
              <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 16, color: "#1A1A1A", margin: 0 }}>{activeFormType.label}</h2>
            </div>
            <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>What's included</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {activeFormType.fields.map((f, i) => (
                <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: i < activeFormType.fields.length - 1 ? "1px solid #F5F5F5" : "none" }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#CCC", flexShrink: 0 }} />
                  <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#555" }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Submissions list */}
          <div>
            {typeLeads.length === 0 ? (
              <div style={{ background: "#fff", borderRadius: 14, padding: "60px 32px", border: "1px solid #E8E8E8", textAlign: "center" }}>
                <Inbox size={36} color="#DDD" style={{ margin: "0 auto 12px" }} />
                <p style={{ fontFamily: "Inter, sans-serif", fontSize: 15, color: "#888", margin: 0 }}>No {activeFormType.label.toLowerCase()} submissions yet.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {typeLeads.map(lead => {
                  const expanded = expandedId === lead.id;
                  const data = parseFormData(lead);
                  return (
                    <div key={lead.id} style={{ background: "#fff", borderRadius: 14, border: "1px solid #E8E8E8", overflow: "hidden" }}>
                      {/* Collapsed header */}
                      <div
                        onClick={() => setExpandedId(expanded ? null : lead.id)}
                        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", background: "none", cursor: "pointer", textAlign: "left" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F7F7F7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "#1A1A1A" }}>
                              {(lead.full_name || "?").charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <p style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {lead.full_name || "Unknown"}
                              </p>
                              {isPartial(lead) && (
                                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 10, fontWeight: 700, color: "#4A6DC7", background: "#E8EDFF", borderRadius: 100, padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>Partial</span>
                              )}
                            </div>
                            <p style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#888", margin: "3px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {lead.email || "No email"}
                            </p>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#AAA", whiteSpace: "nowrap" }}>
                            {formatDate(lead.created_date)}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setArchived(lead, !lead.archived); }}
                            title={lead.archived ? "Unarchive" : "Archive"}
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, border: "1px solid #E5E5E5", background: "#fff", cursor: "pointer", color: lead.archived ? "#1A1A1A" : "#999" }}
                          >
                            {lead.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                          </button>
                          <ChevronDown size={16} color="#888" style={{ transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "none" }} />
                        </div>
                      </div>

                      {/* Expanded details */}
                      {expanded && (
                        <div style={{ padding: "0 24px 20px", borderTop: "1px solid #F0F0F0" }}>
                          {/* Quick contact bar */}
                          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", paddingTop: 16 }}>
                            {lead.email && (
                              <a href={`mailto:${lead.email}`} style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "Inter, sans-serif", fontSize: 13, color: "#1A1A1A", textDecoration: "none" }}>
                                <Mail size={13} color="#888" /> {lead.email}
                              </a>
                            )}
                            {lead.phone && (
                              <span style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "Inter, sans-serif", fontSize: 13, color: "#555" }}>
                                <Phone size={13} color="#888" /> {lead.phone}
                              </span>
                            )}
                            {lead.source_page && (
                              <span style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "Inter, sans-serif", fontSize: 13, color: "#555" }}>
                                <Clock size={13} color="#888" /> {lead.source_page}
                              </span>
                            )}
                          </div>

                          {/* All form fields */}
                          <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", margin: "20px 0 10px" }}>Submitted data</p>
                          <div style={{ background: "#FAFAFA", borderRadius: 10, padding: "16px 20px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }} className="fs-fields-grid">
                              {Object.entries(data).map(([key, value]) => {
                                const fieldDef = activeFormType.fields.find(f => f.key === key);
                                const label = fieldDef?.label || key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase());
                                return (
                                  <div key={key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, color: "#AAA", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#1A1A1A", wordBreak: "break-word", lineHeight: 1.5 }}>{renderFieldValue(value)}</span>
                                  </div>
                                );
                              })}
                              {Object.keys(data).length === 0 && (
                                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#888" }}>No detailed data available.</span>
                              )}
                            </div>
                          </div>

                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .fs-layout { grid-template-columns: 1fr !important; }
          .fs-fields-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}